import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { IMAGES } from "@/lib/images";
import lubaHero from "@/assets/luba-hero.webp";
import { ProductCard, ProductImage, formatPrice } from "@/components/ProductCard";
import { EnquiryDialog } from "@/components/EnquiryDialog";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { SlidersHorizontal, X, Scale } from "lucide-react";

const AREA_BANDS = [
  { label: "All areas", value: "all" },
  { label: "Up to 500 m²", value: "0-500" },
  { label: "500 – 1,500 m²", value: "500-1500" },
  { label: "1,500 – 5,000 m²", value: "1500-5000" },
  { label: "5,000 m²+", value: "5000-999999" },
];
const SLOPE_BANDS = [
  { label: "All slopes", value: "all" },
  { label: "Up to 45%", value: "45" },
  { label: "Up to 80%", value: "80" },
];

export default function Domestic() {
  const [products, setProducts] = useState([]);
  const [brand, setBrand] = useState("all");
  const [area, setArea] = useState("all");
  const [slope, setSlope] = useState("all");
  const [compare, setCompare] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);
  const [enq, setEnq] = useState({ open: false, product: null });

  useEffect(() => {
    api.get("/products", { params: { category: "domestic" } })
      .then((res) => setProducts(res.data))
      .catch(() => {});
  }, []);

  const brands = useMemo(() => {
    const s = new Set(products.map((p) => p.brand).filter(Boolean));
    return Array.from(s).sort();
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (brand !== "all" && p.brand !== brand) return false;
      if (area !== "all") {
        const [min, max] = area.split("-").map(Number);
        if (p.area_m2 == null || p.area_m2 < min || p.area_m2 > max) return false;
      }
      if (slope !== "all") {
        if (p.max_slope == null || p.max_slope > Number(slope)) return false;
      }
      return true;
    });
  }, [products, brand, area, slope]);

  const resetFilters = () => { setBrand("all"); setArea("all"); setSlope("all"); };

  const toggleCompare = (product) => {
    setCompare((prev) => {
      const exists = prev.find((p) => p.slug === product.slug);
      if (exists) return prev.filter((p) => p.slug !== product.slug);
      if (prev.length >= 3) {
        toast.error("You can compare up to 3 mowers.");
        return prev;
      }
      return [...prev, product];
    });
  };

  return (
    <div>
      <section className="relative bg-brand-navy text-white">
        <div className="absolute inset-0 opacity-40">
          <img src={lubaHero} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-navy via-brand-navy/70 to-brand-navy/20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <span className="font-mono-spec text-xs uppercase tracking-widest text-white">Domestic Lawncare</span>
          <h1 className="mt-3 font-display font-extrabold tracking-tight text-4xl sm:text-5xl">Find your perfect robotic mower</h1>
          <p className="mt-5 max-w-2xl text-lg text-white/80">Filter by brand, lawn size and slope to compare models. Every mower comes with expert advice, installation and ongoing support.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        {/* Filters */}
        <div className="rounded-2xl border border-brand-navy/10 bg-white p-5 md:p-6">
          <div className="flex items-center gap-2 text-brand-navy font-display font-semibold">
            <SlidersHorizontal className="h-5 w-5" /> Filter mowers
          </div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">Brand</label>
              <Select value={brand} onValueChange={setBrand}>
                <SelectTrigger data-testid="filter-brand-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All brands</SelectItem>
                  {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">Lawn size</label>
              <Select value={area} onValueChange={setArea}>
                <SelectTrigger data-testid="filter-area-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {AREA_BANDS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-mono-spec uppercase tracking-widest text-muted-foreground">Max slope</label>
              <Select value={slope} onValueChange={setSlope}>
                <SelectTrigger data-testid="filter-slope-select" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SLOPE_BANDS.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-muted-foreground" data-testid="results-count">{filtered.length} {filtered.length === 1 ? "mower" : "mowers"}</p>
            <Button variant="ghost" size="sm" onClick={resetFilters} className="text-brand-navy" data-testid="reset-filters-button">Reset filters</Button>
          </div>
        </div>

        {/* Grid */}
        {filtered.length === 0 ? (
          <div className="mt-14 text-center py-16 rounded-2xl border border-dashed border-brand-navy/20 bg-white" data-testid="empty-state">
            <h3 className="font-display font-semibold text-xl text-brand-ink">No mowers match your filters</h3>
            <p className="mt-2 text-muted-foreground">Try widening your lawn size or slope selection.</p>
            <Button onClick={resetFilters} className="mt-5 bg-brand-navy hover:bg-brand-navy/90 text-white">Reset filters</Button>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <ProductCard
                key={p.slug}
                product={p}
                onEnquire={(prod) => setEnq({ open: true, product: prod })}
                compareEnabled
                isCompared={!!compare.find((c) => c.slug === p.slug)}
                onToggleCompare={toggleCompare}
              />
            ))}
          </div>
        )}
      </section>

      {/* Need a repair */}
      <section className="bg-brand-white border-t border-brand-navy/10" data-testid="domestic-repair-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10">
          <div>
            <span className="font-mono-spec text-xs uppercase tracking-widest text-brand-navy">Service &amp; Repairs</span>
            <h2 className="mt-3 font-display font-bold tracking-tight text-2xl sm:text-3xl text-brand-ink">Need a Repair?</h2>
            <p className="mt-4 text-muted-foreground">You don't need to wait for your annual service if your mower develops a problem. Our workshop provides professional diagnostics and repair services for robotic lawnmowers from leading manufacturers.</p>
            <p className="mt-3 text-muted-foreground">If you're experiencing an error, charging problem, unusual noise, cutting issue, battery problem or another fault, contact our team and we'll advise you on the next step.</p>
          </div>
          <div className="rounded-3xl bg-brand-navy text-white p-8 md:p-10 flex flex-col justify-center">
            <h3 className="font-display font-bold text-2xl">Look After Your Mower Before Something Goes Wrong</h3>
            <p className="mt-3 text-white/80">Regular professional servicing gives us the opportunity to identify wear and developing issues that may otherwise go unnoticed. Book your Annual Service or speak to us about a Maintenance Plan.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button asChild className="bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="domestic-book-service"><Link to="/service">Book an Annual Service</Link></Button>
              <Button onClick={() => setEnq({ open: true, product: null })} variant="outline" className="border-white/30 bg-white/5 text-white hover:bg-white hover:text-brand-navy" data-testid="domestic-request-repair">Request a repair</Button>
            </div>
          </div>
        </div>
      </section>

      {/* Compare tray */}
      {compare.length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 w-[92vw] max-w-2xl">
          <div className="rounded-2xl bg-brand-ink text-white shadow-2xl px-4 py-3 flex items-center justify-between gap-3" data-testid="compare-tray">
            <div className="flex items-center gap-3 min-w-0">
              <Scale className="h-5 w-5 text-white shrink-0" />
              <span className="text-sm truncate">{compare.map((c) => c.name).join(", ")}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button size="sm" onClick={() => setCompareOpen(true)} className="bg-white hover:bg-white/90 text-brand-navy font-semibold" data-testid="compare-open-button">Compare ({compare.length})</Button>
              <Button size="icon" variant="ghost" onClick={() => setCompare([])} className="text-white hover:bg-white/10" data-testid="compare-clear-button"><X className="h-4 w-4" /></Button>
            </div>
          </div>
        </div>
      )}

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="compare-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl text-brand-navy">Compare mowers</DialogTitle></DialogHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                <tr>
                  <td className="p-2" />
                  {compare.map((c) => (
                    <td key={c.slug} className="p-2 text-center align-top">
                      <ProductImage src={c.image_url} alt={c.name} className="h-24 w-full" />
                      <div className="mt-2 font-semibold text-brand-ink">{c.name}</div>
                    </td>
                  ))}
                </tr>
                {[
                  ["Brand", (c) => c.brand || "—"],
                  ["Price", (c) => formatPrice(c.price) || "Enquire"],
                  ["Lawn size", (c) => (c.area_m2 != null ? `${c.area_m2.toLocaleString("en-IE")} m²` : "—")],
                  ["Max slope", (c) => (c.max_slope != null ? `${c.max_slope}%` : "—")],
                ].map(([label, fn]) => (
                  <tr key={label} className="border-t border-brand-navy/10">
                    <td className="p-2 font-mono-spec text-xs uppercase tracking-widest text-muted-foreground">{label}</td>
                    {compare.map((c) => <td key={c.slug} className="p-2 text-center text-brand-ink">{fn(c)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      <EnquiryDialog open={enq.open} onOpenChange={(o) => setEnq((e) => ({ ...e, open: o }))} type="domestic" product={enq.product} sourcePage="domestic" />
    </div>
  );
}
